import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { IconName, ButtonVariant, DropdownOptionGroup, PopoverPosition, DropdownSelectionType, DropdownOptionDefault, DropdownOptionType, DropdownOption } from '@yupma/ui-kit';
import { FieldOption } from 'server-model';

@Component({
  selector: 'app-multi-select-dropdown-input',
  templateUrl: './multi-select-dropdown-input.component.html',
  styleUrl: './multi-select-dropdown-input.component.scss'
})
export class MultiSelectDropdownInputComponent implements OnInit, OnChanges {
  @Input() label: string = '';
  @Input() name: string = '';
  @Input() value: string = '';
  @Input() required: boolean = false;
  @Input() hideOptional: boolean = false;
  @Input() error: boolean = false;
  @Input() disabled: boolean = false;
  @Input() inlineInfo: string = '';
  @Input() errorText: string = 'This field is required.';
  @Input() options: FieldOption[] = [];
  @Input() sortAlphabetically: boolean = false;
  @Input() showLabel: boolean = true;
  @Output() valueChange = new EventEmitter<string>();

  isDropdownOpen: boolean = false;
  selectedOptionLabel: string = '';
  selectedOptionValue: string = '';
  initialValue: string = ''; // Add this to track the initial value
  iconNames = IconName;
  buttonVariants = ButtonVariant;

  dropdownOptions: DropdownOptionGroup[] = [];
  filteredDropdownOptions: DropdownOptionGroup[] = [];
  popoverPositions = PopoverPosition;
  dropdownSelectionTypes = DropdownSelectionType;

  constructor() { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value']) {
      // Store the initial value when it changes
      this.initialValue = this.value || '';
      this.setSelectedOptions();
    }
    if (changes['options']) {
      this.createDropdowns();
      this.setSelectedOptions();
    }
  }

  ngOnInit() {
    this.createDropdowns();
    this.setSelectedOptions();
    // Store the initial value on initialization
    this.initialValue = this.value || '';
  }

  private createDropdowns() {
    const dropdownOptions = this.options.map(option => {
      return new DropdownOptionDefault(
        DropdownOptionType.DEFAULT,
        option.label,
        option.value
      );
    });

    if (this.sortAlphabetically) {
      dropdownOptions.sort((a, b) => a.label.localeCompare(b.label));
    }

    const dropdownGroup = new DropdownOptionGroup('', dropdownOptions);
    this.dropdownOptions = [dropdownGroup];
    this.filteredDropdownOptions = [dropdownGroup];
  }

  private setSelectedOptions() {
    const selectedValues = this.value ? this.value.split(';').filter(val => val.trim() !== '') : [''];
    const selectedOptions: DropdownOption[] = [];

    this.filteredDropdownOptions.forEach(group => {
      group.options.forEach(option => {
        let isSelected = false;
        for (const selectedValue of selectedValues) {
          if (option.value === selectedValue) {
            isSelected = true;
            break;
          }
        }
        option.selected = isSelected;
        if (isSelected) {
          selectedOptions.push(option);
        }
      });
    });
    this.selectedOptionLabel = selectedOptions.map(item => item.label).join(', ');
    this.selectedOptionValue = selectedOptions.map(item => item.value).join(';');
  }

  toggleDropdown(state: boolean) {
    this.isDropdownOpen = state;
    this.filteredDropdownOptions = this.dropdownOptions;
    
    if (state === false) {
      // Only emit if the value has actually changed
      if (this.selectedOptionValue !== this.initialValue) {
        this.valueChange.emit(this.selectedOptionValue);
        // Update the initial value to the new value
        this.initialValue = this.selectedOptionValue;
      }
    }
  }

  selectedOption(selectedOption: DropdownOption) {
    selectedOption.selected = !selectedOption.selected;
    this.updateSelectedOptionLabel();
  }

  private updateSelectedOptionLabel(): void {
    const selectedOptions: DropdownOption[] = [];
    this.dropdownOptions.forEach(group => {
      group.options.forEach(option => {
        if (option.selected === true) {
          selectedOptions.push(option);
        }
      });
    });
    this.selectedOptionLabel = selectedOptions.map(item => item.label).join(', ');
    this.selectedOptionValue = selectedOptions.map(item => item.value).join(';');
  }

  search(query: string) {
    const lowerQuery = query.toLowerCase();

    // Filter the options based on the search query
    this.filteredDropdownOptions = this.dropdownOptions.map(group => {
      const filteredOptions = group.options.filter(option => {
        return option.label.toLowerCase().includes(lowerQuery);
      });

      return new DropdownOptionGroup(group.text, filteredOptions);
    }).filter(group => group.options.length > 0);
  }
}